import {lazy, Suspense, useMemo, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import {useSuiContext} from '@s-ui/react-context'
import useMount from '@s-ui/react-hooks/lib/useMount'
import MoleculeModal, {MoleculeModalContent, MoleculeModalFooter, MoleculeModalSizes} from '@s-ui/react-molecule-modal'
import ListFilters, {listFiltersPlaces} from '@adv-mt/mt-list-filters'
import {spinnerBasicOverlayTypes} from '@adv-mt/mt-spinner-basic'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'

import {enrichUserAlertWithVehicleNames} from './utils.js'

const BASE_CLASS = 'mt-modalUpdateAlert'

const SpinnerBasic = lazy(() => import(/* webpackChunkName: "SpinnerBasic" */ '@adv-mt/mt-spinner-basic'))

const IconCloseModal = () => <IconClose size={ATOM_ICON_SIZES.medium} />

const ModalUpdateAlert = ({isOpen = false, onClose, onConfirm, pageName, updateData: userAlert = {}}) => {
  const {domain, i18n} = useSuiContext()

  const config = domain.get('config')

  const {LISTING_SECTION_IDS_BY_CATEGORIES} = config.get('stock')
  const {FILTER_NAMES} = config.get('searchFilters')

  const [currentUserAlert, setCurrentUserAlert] = useState(userAlert)
  const [listFiltersProps, setListFiltersProps] = useState()
  const [isLoading, setIsLoading] = useState(false)

  const enrichedUserAlertWithVehicleNames = useMemo(
    () =>
      enrichUserAlertWithVehicleNames({
        makesWithModels: listFiltersProps?.[FILTER_NAMES.VEHICLES]?.options || [],
        userAlert: currentUserAlert
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserAlert, listFiltersProps]
  )

  useMount(async () => {
    setIsLoading(true)
    const [, makesWithModels] = await domain.get('get_makes_with_models_vehicles_use_case').execute()

    if (makesWithModels) {
      setListFiltersProps({
        [FILTER_NAMES.VEHICLES]: {
          options: makesWithModels.value.map(({make, models = []}) => ({
            id: make.id,
            label: make.name,
            models: models.map(({id, name}) => ({id, label: name}))
          }))
        }
      })
    }
    setIsLoading(false)
  })

  const handleOnFilter = ({search}) => {
    const {page, ...filters} = search
    setCurrentUserAlert({...enrichedUserAlertWithVehicleNames, filters})
  }

  return isLoading ? (
    <Suspense fallback={null}>
      <SpinnerBasic overlayType={spinnerBasicOverlayTypes.LIGHT} />
    </Suspense>
  ) : (
    <MoleculeModal
      closeOnEscKeyDown
      closeOnOutsideClick
      header={i18n.t('SEARCH_SAVE_UPDATE')}
      iconClose={<IconCloseModal />}
      isContentless
      isOpen={isOpen}
      onClose={onClose}
      size={MoleculeModalSizes.SMALL}
    >
      {!isLoading && (
        <MoleculeModalContent>
          <div className={`${BASE_CLASS}-content`}>
            <div className={`${BASE_CLASS}-updatedFilters`}>
              <ListFilters
                filtersProps={listFiltersProps}
                hasUserInteractionWithFilters
                listingSectionId={
                  LISTING_SECTION_IDS_BY_CATEGORIES[enrichedUserAlertWithVehicleNames?.filters?.categoryId] ||
                  LISTING_SECTION_IDS_BY_CATEGORIES.default
                }
                onFilter={handleOnFilter}
                pageName={pageName}
                place={listFiltersPlaces.MODAL}
                search={enrichedUserAlertWithVehicleNames?.filters || {}}
              />
            </div>
          </div>
        </MoleculeModalContent>
      )}
      <MoleculeModalFooter>
        <div className={`${BASE_CLASS}-actions`}>
          <AtomButton
            design={atomButtonDesigns.FLAT}
            onClick={onClose}
            size={atomButtonSizes.MEDIUM}
            shape={atomButtonShapes.CIRCULAR}
          >
            {i18n.t('COMMON.CANCEL')}
          </AtomButton>
          <AtomButton
            design={atomButtonDesigns.SOLID}
            onClick={() =>
              onConfirm({
                userAlert: currentUserAlert,
                activeEmail: currentUserAlert.activeEmail,
                activePush: currentUserAlert.activePush
              })
            }
            size={atomButtonSizes.MEDIUM}
            shape={atomButtonShapes.CIRCULAR}
          >
            {i18n.t('SEARCH.UPDATE_SEARCH_CONFIRM')}
          </AtomButton>
        </div>
      </MoleculeModalFooter>
    </MoleculeModal>
  )
}

ModalUpdateAlert.displayName = 'ModalUpdateAlert'
ModalUpdateAlert.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  pageName: PropTypes.string,
  updateData: PropTypes.object
}

export default ModalUpdateAlert
